import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Card,
  Text,
  Title,
  Metric,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Button,
  Grid,
} from '@tremor/react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList } from 'recharts';
import kpiAPI from '../../api/kpi';
import DatePicker from './DatePicker';
import './KPIStatPage.css';

const PROJECT = 'MAN';
const SHEET_POWYZEJ = 'POWYŻEJ24H';
const SHEET_POJEMNIKI = 'POJEMNIKI';
const SHEET_HISTORIA = 'POWYŻEJ24H_HISTORIA';
const STORAGE_SNAPSHOT = 'kpi-przyjecie-snapshot';
const STORAGE_KEY_DATE_FROM = 'przyjecie-dateFrom';
const STORAGE_KEY_DATE_TO = 'przyjecie-dateTo';
const COL_DATA_WPROWADZENIA = 'DATA WPROWADZENIA DANYCH';
const MAX_SNAPSHOT_DATES = 30;

function getInitialPrzyjecieDateFrom() {
  try {
    const s = localStorage.getItem(STORAGE_KEY_DATE_FROM);
    if (s && /^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  } catch (e) {}
  const d = new Date();
  d.setDate(d.getDate() - 13);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getInitialPrzyjecieDateTo() {
  try {
    const s = localStorage.getItem(STORAGE_KEY_DATE_TO);
    if (s && /^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  } catch (e) {}
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Z daty w historii (DD.MM.YYYY lub YYYY-MM-DD) zwraca YYYY-MM-DD lub null. */
function historyDateToYMD(dataStr) {
  if (!dataStr || typeof dataStr !== 'string') return null;
  const s = String(dataStr).trim();
  const iso = /^\d{4}-\d{2}-\d{2}/.exec(s);
  if (iso) return iso[0];
  const ddmmyy = s.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  if (ddmmyy) {
    const [, d, m, y] = ddmmyy;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return null;
}
const CHART_COLORS = { KLT: '#00bcd4', GLT: '#ff9800', przyjete: '#66bb6a' };

/** Etykieta wartości na wykresie – kompaktowa „pigułka” z liczbą */
function ChartValueLabel({ x, y, value, payload, dataKey, color }) {
  const val = value ?? payload?.[dataKey];
  if (val == null || val === '') return null;
  const str = String(Math.round(Number(val)));
  const fill = color || '#f1f5f9';
  const w = Math.max(28, str.length * 10);
  const h = 22;
  return (
    <g transform={`translate(${x - w / 2},${y - h - 6})`}>
      <rect width={w} height={h} rx="11" ry="11" fill="rgba(30,41,59,0.92)" stroke={fill} strokeWidth="1.2" />
      <text x={w / 2} y={h / 2 + 1} textAnchor="middle" dominantBaseline="middle" fill={fill} fontSize="12" fontWeight="600">
        {str}
      </text>
    </g>
  );
}
const TOOLTIP_STYLE = { background: '#1e2433', border: '1px solid #334155', borderRadius: 8 };
const TICK_STYLE = { fill: '#94a3b8', fontSize: 11 };
const GRID_STYLE = { stroke: '#334155', strokeOpacity: 0.6 };

function getCell(row, col) {
  if (col < 0 || !row || !row[col]) return '';
  const cell = row[col];
  if (cell && typeof cell === 'object' && 'value' in cell) return cell.value != null ? String(cell.value).trim() : '';
  return cell != null ? String(cell).trim() : '';
}

function getColumnIndex(headers, name) {
  const needle = String(name || '').trim().toLowerCase();
  const idx = headers.findIndex((h) => String(h || '').trim().toLowerCase() === needle);
  return idx >= 0 ? idx : -1;
}

function parseDateFromCell(val) {
  if (val == null || val === '') return null;
  const s = String(val).trim();
  if (!s) return null;
  if (typeof val === 'number' && val > 0) {
    const d = new Date((val - 25569) * 86400 * 1000);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const datePart = s.includes(' ') ? s.split(/\s/)[0] : s;
  const iso = /^\d{4}-\d{2}-\d{2}/.exec(datePart);
  if (iso) return new Date(iso[0]);
  const ddmmyy = datePart.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  if (ddmmyy) return new Date(parseInt(ddmmyy[3], 10), parseInt(ddmmyy[2], 10) - 1, parseInt(ddmmyy[1], 10));
  const d = new Date(datePart);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateDisplay(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

function normalizeSheetData(data) {
  if (!data || !Array.isArray(data)) return [];
  return data.map((row) => {
    if (!Array.isArray(row)) return [];
    return row.map((cell) => {
      if (cell && typeof cell === 'object' && 'value' in cell) return cell;
      return { value: cell != null ? String(cell) : '' };
    });
  });
}

function getTodayStart() {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return t;
}

function dateToYMD(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function ymdToDDMMYYYY(ymd) {
  if (!ymd || typeof ymd !== 'string') return '';
  const [y, m, d] = ymd.split('-');
  if (!d || !m || !y) return ymd;
  return `${d}.${m}.${y}`;
}

/** Z wartości DATA w historii (DD.MM.YYYY lub YYYY-MM-DD) zwraca krótki format DD.MM do wykresu. */
function formatHistoryDateShort(dataStr) {
  const ymd = historyDateToYMD(dataStr);
  if (!ymd) return String(dataStr || '');
  const [y, m, d] = ymd.split('-');
  return `${d}.${m}`;
}

function Przyjecie() {
  const [sheets, setSheets] = useState({ powyzej: null, pojemniki: null, historia: null });
  const [sheetsListRef, setSheetsListRef] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snapshotsByDate, setSnapshotsByDate] = useState(null);
  const lastSavedHistoryPayloadRef = useRef(null);
  const [dateFrom, setDateFrom] = useState(getInitialPrzyjecieDateFrom);
  const [dateTo, setDateTo] = useState(getInitialPrzyjecieDateTo);

  const loadReports = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await kpiAPI.getReports(PROJECT);
      const list = Array.isArray(res?.sheets) ? res.sheets : [];
      setSheetsListRef(list);
      const byName = (name) => list.find((s) => String(s.name || '').trim().toLowerCase() === String(name || '').trim().toLowerCase());
      const powyzej = byName(SHEET_POWYZEJ);
      const pojemniki = byName(SHEET_POJEMNIKI);
      const historiaSheet = byName(SHEET_HISTORIA);
      const historiaData = historiaSheet ? normalizeSheetData(historiaSheet.data) : [];
      setSheets({
        powyzej: powyzej ? { name: powyzej.name, data: normalizeSheetData(powyzej.data) } : null,
        pojemniki: pojemniki ? { name: pojemniki.name, data: normalizeSheetData(pojemniki.data) } : null,
        historia: historiaSheet ? { name: historiaSheet.name, id: historiaSheet.id, data: historiaData } : null,
      });
      if (!powyzej) setError('Brak arkusza „POWYŻEJ24H” w Uzupełnij raport.');
    } catch (err) {
      console.error('Błąd ładowania PRZYJĘCIE:', err);
      setError('Nie udało się załadować danych.');
      setSheets({ powyzej: null, pojemniki: null, historia: null });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_SNAPSHOT);
      if (!raw) {
        setSnapshotsByDate(null);
        return;
      }
      const parsed = JSON.parse(raw);
      if (parsed && parsed.byDate && typeof parsed.byDate === 'object') {
        setSnapshotsByDate(parsed.byDate);
        return;
      }
      if (parsed && Array.isArray(parsed.keys) && parsed.date) {
        const byDate = { [parsed.date]: { keys: parsed.keys } };
        setSnapshotsByDate(byDate);
        try {
          localStorage.setItem(STORAGE_SNAPSHOT, JSON.stringify({ byDate }));
        } catch (e2) {}
        return;
      }
      setSnapshotsByDate(null);
    } catch (e) {
      setSnapshotsByDate(null);
    }
  }, []);

  const kltSet = useMemo(() => {
    const p = sheets.pojemniki?.data;
    if (!p || !p.length) return new Set();
    const headerRow = p[0].map((_, i) => getCell(p[0], i));
    const pojCol = getColumnIndex(headerRow, 'POJEMNIK');
    if (pojCol < 0) return new Set();
    const set = new Set();
    for (let r = 1; r < p.length; r++) {
      const v = getCell(p[r], pojCol);
      if (v) set.add(v);
    }
    return set;
  }, [sheets.pojemniki]);

  const headersPowyzej = useMemo(() => {
    const d = sheets.powyzej?.data;
    if (!d || !d[0]) return [];
    return d[0].map((c, i) => getCell(d[0], i));
  }, [sheets.powyzej]);

  const dataRowsPowyzej = useMemo(() => sheets.powyzej?.data?.slice(1) ?? [], [sheets.powyzej]);

  const idxSnr = getColumnIndex(headersPowyzej, 'SNR');
  const idxHu = getColumnIndex(headersPowyzej, 'HU');
  const idxPojemnik = getColumnIndex(headersPowyzej, 'POJEMNIK');
  const idxDataPrzyjecia = getColumnIndex(headersPowyzej, 'DATA PRZYJĘCIA');
  const idxDataWprowadzenia = getColumnIndex(headersPowyzej, COL_DATA_WPROWADZENIA);

  const todayStart = getTodayStart();

  const dataOstatniegoWklejenia = useMemo(() => {
    if (idxDataWprowadzenia < 0) return null;
    let maxDate = null;
    let lastSeenYmd = null;
    dataRowsPowyzej.forEach((row) => {
      const val = getCell(row, idxDataWprowadzenia);
      const d = parseDateFromCell(val || (row[idxDataWprowadzenia] && row[idxDataWprowadzenia].value));
      if (d && !Number.isNaN(d.getTime())) {
        lastSeenYmd = dateToYMD(d);
      }
      if (lastSeenYmd && (!maxDate || lastSeenYmd > maxDate)) maxDate = lastSeenYmd;
    });
    return maxDate;
  }, [dataRowsPowyzej, idxDataWprowadzenia]);

  const {
    kltCount,
    gltCount,
    top3Oldest,
    sumyPerSnr,
    currentKeysSet,
    keysByEffectiveDate,
    distinctDatesSorted,
    unikalneSnrCount,
  } = useMemo(() => {
    const kltUnique = new Set();
    const gltUnique = new Set();
    const bySnrMagazynOldest = new Map();
    const bySnrMagazynUniqueKeys = new Map();
    const currentKeys = new Set();
    const keysByDate = {};
    let lastSeenYmd = null;
    let unikalneSnrCount = 0;
    const snrSet = new Set();
    const onlyLastPaste = dataOstatniegoWklejenia != null;

    if (idxSnr < 0 || idxHu < 0 || idxDataPrzyjecia < 0) {
      return {
        kltCount: 0,
        gltCount: 0,
        top3Oldest: [],
        sumyPerSnr: [],
        currentKeysSet: currentKeys,
        keysByEffectiveDate: keysByDate,
        distinctDatesSorted: [],
        unikalneSnrCount: 0,
      };
    }

    dataRowsPowyzej.forEach((row) => {
      if (idxDataWprowadzenia >= 0) {
        const wpVal = getCell(row, idxDataWprowadzenia);
        const dw = parseDateFromCell(wpVal || (row[idxDataWprowadzenia] && row[idxDataWprowadzenia].value));
        if (dw && !Number.isNaN(dw.getTime())) lastSeenYmd = dateToYMD(dw);
      }
      const snr = getCell(row, idxSnr);
      const hu = getCell(row, idxHu);
      const pojemnik = getCell(row, idxPojemnik);
      const dateVal = getCell(row, idxDataPrzyjecia);
      const d = parseDateFromCell(dateVal || (row[idxDataPrzyjecia] && row[idxDataPrzyjecia].value));
      if (!d || Number.isNaN(d.getTime())) return;
      if (d >= todayStart) return;
      const magazyn = idxPojemnik >= 0 && kltSet.has(pojemnik) ? 'KLT' : 'GLT';
      const key = `${snr}|${hu}`;
      const fromLastPaste = !onlyLastPaste || lastSeenYmd === dataOstatniegoWklejenia;
      if (fromLastPaste) {
        currentKeys.add(key);
        if (magazyn === 'KLT') kltUnique.add(key);
        else gltUnique.add(key);
        if (snr) snrSet.add(snr);
      }
      if (lastSeenYmd) {
        if (!keysByDate[lastSeenYmd]) keysByDate[lastSeenYmd] = { keys: new Set(), keyToMagazyn: {} };
        keysByDate[lastSeenYmd].keys.add(key);
        keysByDate[lastSeenYmd].keyToMagazyn[key] = magazyn;
      }
      if (fromLastPaste) {
        const posKey = `${snr}|${magazyn}`;
        const prev = bySnrMagazynOldest.get(posKey);
        if (!prev || d < prev) bySnrMagazynOldest.set(posKey, d);
        if (!bySnrMagazynUniqueKeys.has(posKey)) bySnrMagazynUniqueKeys.set(posKey, new Set());
        bySnrMagazynUniqueKeys.get(posKey).add(key);
      }
    });

    const top3 = Array.from(bySnrMagazynOldest.entries())
      .map(([posKey, date]) => {
        const [snr, magazyn] = posKey.split('|');
        return { snr, magazyn, date, posKey };
      })
      .sort((a, b) => a.date - b.date)
      .slice(0, 10);

    const sumyPerSnr = Array.from(bySnrMagazynUniqueKeys.entries())
      .map(([posKey, keySet]) => {
        const [snr, magazyn] = posKey.split('|');
        return { snr, magazyn, liczba: keySet.size };
      })
      .sort((a, b) => (b.liczba - a.liczba) || String(a.snr).localeCompare(b.snr));

    const distinctDatesSorted = Object.keys(keysByDate).filter((d) => keysByDate[d].keys.size > 0).sort();
    unikalneSnrCount = snrSet.size;

    return {
      kltCount: kltUnique.size,
      gltCount: gltUnique.size,
      top3Oldest: top3,
      sumyPerSnr,
      currentKeysSet: currentKeys,
      keysByEffectiveDate: keysByDate,
      distinctDatesSorted,
      unikalneSnrCount,
    };
  }, [dataRowsPowyzej, idxSnr, idxHu, idxPojemnik, idxDataPrzyjecia, idxDataWprowadzenia, kltSet, dataOstatniegoWklejenia]);

  const najstarszaDataStr = useMemo(() => {
    if (!top3Oldest.length) return '';
    const d = top3Oldest[0].date;
    return d ? formatDateDisplay(d) : '';
  }, [top3Oldest]);

  const historyRows = useMemo(() => {
    const h = sheets.historia?.data;
    if (!h || !h.length) return [];
    const headerRow = h[0].map((c, i) => getCell(h[0], i));
    const idxData = getColumnIndex(headerRow, 'DATA');
    const idxKlt = getColumnIndex(headerRow, 'KLT');
    const idxGlt = getColumnIndex(headerRow, 'GLT');
    const idxRazem = getColumnIndex(headerRow, 'RAZEM');
    const idxPrzyjete = getColumnIndex(headerRow, 'PRZYJĘTE_OD_POPRZEDNIEGO');
    const idxPrzyjeteKlt = getColumnIndex(headerRow, 'PRZYJĘTE_KLT');
    const idxPrzyjeteGlt = getColumnIndex(headerRow, 'PRZYJĘTE_GLT');
    const idxNajstarsza = getColumnIndex(headerRow, 'NAJSTARSZA_DATA');
    const idxUnikalne = getColumnIndex(headerRow, 'UNIKALNE_SNR');
    const idxNotatka = getColumnIndex(headerRow, 'NOTATKA');
    const rows = [];
    for (let r = 1; r < h.length; r++) {
      const row = h[r];
      const dataVal = idxData >= 0 ? getCell(row, idxData) : '';
      if (!dataVal) continue;
      rows.push({
        data: dataVal,
        klt: idxKlt >= 0 ? parseInt(getCell(row, idxKlt), 10) || 0 : 0,
        glt: idxGlt >= 0 ? parseInt(getCell(row, idxGlt), 10) || 0 : 0,
        razem: idxRazem >= 0 ? parseInt(getCell(row, idxRazem), 10) || 0 : 0,
        przyjeteOdPoprzedniego: idxPrzyjete >= 0 ? parseInt(getCell(row, idxPrzyjete), 10) || 0 : 0,
        przyjeteKlt: idxPrzyjeteKlt >= 0 ? parseInt(getCell(row, idxPrzyjeteKlt), 10) || 0 : 0,
        przyjeteGlt: idxPrzyjeteGlt >= 0 ? parseInt(getCell(row, idxPrzyjeteGlt), 10) || 0 : 0,
        najstarszaData: idxNajstarsza >= 0 ? getCell(row, idxNajstarsza) : '',
        unikalneSnr: idxUnikalne >= 0 ? parseInt(getCell(row, idxUnikalne), 10) || 0 : 0,
        notatka: idxNotatka >= 0 ? getCell(row, idxNotatka) : '',
      });
    }
    return rows.sort((a, b) => (a.data < b.data ? 1 : a.data > b.data ? -1 : 0));
  }, [sheets.historia]);

  const historyRowsInRange = useMemo(() => {
    if (!dateFrom || !dateTo || dateFrom > dateTo) return [];
    return historyRows
      .filter((r) => {
        const ymd = historyDateToYMD(r.data);
        return ymd && ymd >= dateFrom && ymd <= dateTo;
      })
      .sort((a, b) => {
        const ya = historyDateToYMD(a.data);
        const yb = historyDateToYMD(b.data);
        return !ya || !yb ? 0 : ya < yb ? -1 : ya > yb ? 1 : 0;
      });
  }, [historyRows, dateFrom, dateTo]);

  const singleDayRow = useMemo(() => {
    if (dateFrom !== dateTo || historyRowsInRange.length !== 1) return null;
    return historyRowsInRange[0];
  }, [dateFrom, dateTo, historyRowsInRange]);

  useEffect(() => {
    try {
      if (dateFrom) localStorage.setItem(STORAGE_KEY_DATE_FROM, dateFrom);
      if (dateTo) localStorage.setItem(STORAGE_KEY_DATE_TO, dateTo);
    } catch (e) {}
  }, [dateFrom, dateTo]);

  const referenceDate = useMemo(() => {
    if (distinctDatesSorted.length >= 2) {
      return distinctDatesSorted[distinctDatesSorted.length - 2];
    }
    if (!snapshotsByDate || typeof snapshotsByDate !== 'object') return null;
    const dates = Object.keys(snapshotsByDate).filter((d) => snapshotsByDate[d] && Array.isArray(snapshotsByDate[d].keys)).sort();
    if (!dates.length) return null;
    if (dataOstatniegoWklejenia) {
      const prev = dates.filter((d) => d < dataOstatniegoWklejenia);
      return prev.length ? prev[prev.length - 1] : null;
    }
    return dates[dates.length - 1];
  }, [distinctDatesSorted, snapshotsByDate, dataOstatniegoWklejenia]);

  const { przyjeteCount, przyjeteLista, przyjeteKlt, przyjeteGlt } = useMemo(() => {
    let klt = 0;
    let glt = 0;
    if (distinctDatesSorted.length >= 2 && keysByEffectiveDate) {
      const prevDate = distinctDatesSorted[distinctDatesSorted.length - 2];
      const currDate = distinctDatesSorted[distinctDatesSorted.length - 1];
      const prev = keysByEffectiveDate[prevDate];
      const currKeys = keysByEffectiveDate[currDate]?.keys;
      if (prev && prev.keys && currKeys) {
        const list = [];
        prev.keys.forEach((k) => {
          if (!currKeys.has(k)) {
            const parts = String(k).split('|');
            list.push({ snr: parts[0] || '', hu: parts[1] || '' });
            const mag = prev.keyToMagazyn && prev.keyToMagazyn[k];
            if (mag === 'KLT') klt++;
            else if (mag === 'GLT') glt++;
          }
        });
        return { przyjeteCount: list.length, przyjeteLista: list, przyjeteKlt: klt, przyjeteGlt: glt };
      }
    }
    if (!referenceDate || !snapshotsByDate || !snapshotsByDate[referenceDate] || !Array.isArray(snapshotsByDate[referenceDate].keys)) {
      return { przyjeteCount: 0, przyjeteLista: [], przyjeteKlt: 0, przyjeteGlt: 0 };
    }
    const list = [];
    snapshotsByDate[referenceDate].keys.forEach((k) => {
      if (!currentKeysSet.has(k)) {
        const parts = String(k).split('|');
        list.push({ snr: parts[0] || '', hu: parts[1] || '' });
      }
    });
    return { przyjeteCount: list.length, przyjeteLista: list, przyjeteKlt: 0, przyjeteGlt: 0 };
  }, [distinctDatesSorted, keysByEffectiveDate, snapshotsByDate, referenceDate, currentKeysSet]);

  const historyRowPayload = useMemo(
    () =>
      dataOstatniegoWklejenia
        ? {
            dataStr: ymdToDDMMYYYY(dataOstatniegoWklejenia),
            klt: kltCount,
            glt: gltCount,
            razem: kltCount + gltCount,
            przyjete: przyjeteCount,
            przyjeteKlt,
            przyjeteGlt,
            najstarszaData: najstarszaDataStr,
            unikalneSnr: unikalneSnrCount,
          }
        : null,
    [
      dataOstatniegoWklejenia,
      kltCount,
      gltCount,
      przyjeteCount,
      przyjeteKlt,
      przyjeteGlt,
      najstarszaDataStr,
      unikalneSnrCount,
    ]
  );

  useEffect(() => {
    if (!historyRowPayload || !sheets.historia || !sheetsListRef || !Array.isArray(sheetsListRef)) return;
    const key = `${historyRowPayload.dataStr}|${historyRowPayload.klt}|${historyRowPayload.glt}|${historyRowPayload.przyjete}`;
    if (lastSavedHistoryPayloadRef.current === key) return;
    lastSavedHistoryPayloadRef.current = key;
    const headerRow = sheets.historia.data?.[0];
    if (!headerRow || !headerRow.length) return;
    const getHeader = (i) => String(getCell(headerRow, i)).trim().toLowerCase();
    const newRow = headerRow.map((_, i) => {
      const h = getHeader(i);
      const v = historyRowPayload;
      if (h === 'data') return { value: v.dataStr };
      if (h === 'klt') return { value: String(v.klt) };
      if (h === 'glt') return { value: String(v.glt) };
      if (h === 'razem') return { value: String(v.razem) };
      if (h === 'przyjęte_od_poprzedniego') return { value: String(v.przyjete) };
      if (h === 'przyjęte_klt') return { value: String(v.przyjeteKlt) };
      if (h === 'przyjęte_glt') return { value: String(v.przyjeteGlt) };
      if (h === 'najstarsza_data') return { value: v.najstarszaData };
      if (h === 'unikalne_snr') return { value: String(v.unikalneSnr) };
      if (h === 'notatka') return { value: '' };
      return { value: '' };
    });
    const historyData = sheets.historia.data;
    const idxDataCol = headerRow.findIndex((c, i) => getHeader(i) === 'data');
    let dataRows = historyData.slice(1);
    const existingIdx =
      idxDataCol >= 0
        ? dataRows.findIndex(
            (row) =>
              getCell(row, idxDataCol) === historyRowPayload.dataStr ||
              getCell(row, idxDataCol) === dataOstatniegoWklejenia
          )
        : -1;
    if (existingIdx >= 0) {
      dataRows = dataRows.map((r, i) => (i === existingIdx ? newRow : r));
    } else {
      dataRows = [newRow, ...dataRows];
    }
    const newHistoryData = [headerRow, ...dataRows];
    const updatedSheets = sheetsListRef.map((s) =>
      String(s.name || '').trim().toLowerCase() === SHEET_HISTORIA.toLowerCase()
        ? { ...s, data: newHistoryData }
        : s
    );
    kpiAPI
      .saveReports(PROJECT, { sheets: updatedSheets })
      .then(() => {
        setSheetsListRef(updatedSheets);
        setSheets((prev) =>
          prev.historia ? { ...prev, historia: { ...prev.historia, data: newHistoryData } } : prev
        );
      })
      .catch((err) => console.error('Zapis historii PRZYJĘCIE:', err));
  }, [historyRowPayload, sheets.historia, sheetsListRef]);

  const currentKeysSerialized = useMemo(
    () => (dataOstatniegoWklejenia && currentKeysSet?.size ? [dataOstatniegoWklejenia, JSON.stringify([...currentKeysSet].sort())] : null),
    [dataOstatniegoWklejenia, currentKeysSet]
  );

  useEffect(() => {
    if (!currentKeysSerialized) return;
    const [dataOstatniegoWklejeniaStr, keysJson] = currentKeysSerialized;
    let keys = [];
    try {
      keys = keysJson ? JSON.parse(keysJson) : [];
    } catch (e) {
      return;
    }
    if (!dataOstatniegoWklejeniaStr || !keys.length) return;
    try {
      const raw = localStorage.getItem(STORAGE_SNAPSHOT);
      const byDate = raw ? (() => {
        try {
          const p = JSON.parse(raw);
          if (p && p.byDate && typeof p.byDate === 'object') return { ...p.byDate };
          if (p && p.date && Array.isArray(p.keys)) return { [p.date]: { keys: p.keys } };
        } catch (e) {}
        return {};
      })() : {};
      byDate[dataOstatniegoWklejeniaStr] = { keys };
      const sorted = Object.keys(byDate).sort();
      if (sorted.length > MAX_SNAPSHOT_DATES) {
        sorted.slice(0, sorted.length - MAX_SNAPSHOT_DATES).forEach((d) => delete byDate[d]);
      }
      localStorage.setItem(STORAGE_SNAPSHOT, JSON.stringify({ byDate }));
      setSnapshotsByDate({ ...byDate });
    } catch (e) {
      console.error('Auto-zapis snapshotu:', e);
    }
  }, [currentKeysSerialized]);

  const saveSnapshot = useCallback(() => {
    const today = dateToYMD(new Date());
    if (!today) return;
    try {
      const raw = localStorage.getItem(STORAGE_SNAPSHOT);
      const byDate = raw ? (() => {
        try {
          const p = JSON.parse(raw);
          if (p && p.byDate && typeof p.byDate === 'object') return { ...p.byDate };
          if (p && p.date && Array.isArray(p.keys)) return { [p.date]: { keys: p.keys } };
        } catch (e) {}
        return {};
      })() : {};
      byDate[today] = { keys: Array.from(currentKeysSet) };
      const sorted = Object.keys(byDate).sort();
      if (sorted.length > MAX_SNAPSHOT_DATES) {
        sorted.slice(0, sorted.length - MAX_SNAPSHOT_DATES).forEach((d) => delete byDate[d]);
      }
      localStorage.setItem(STORAGE_SNAPSHOT, JSON.stringify({ byDate }));
      setSnapshotsByDate({ ...byDate });
    } catch (e) {
      console.error('Zapis snapshotu:', e);
    }
  }, [currentKeysSet]);

  const hasRequiredColumns = idxSnr >= 0 && idxHu >= 0 && idxDataPrzyjecia >= 0;

  if (loading) {
    return (
      <div className="kpi-stat-page">
        <Title className="kpi-ps-header-title">Przyjęcie powyżej 24h</Title>
        <Text className="kpi-ps-table-loading">Ładowanie…</Text>
      </div>
    );
  }

  if (error || !sheets.powyzej) {
    return (
      <div className="kpi-stat-page">
        <Title className="kpi-ps-header-title">Przyjęcie powyżej 24h</Title>
        <Text className="kpi-ps-table-error">{error || 'Brak arkusza „POWYŻEJ24H”.'}</Text>
        <Text className="kpi-ps-table-empty">Dodaj arkusz „POWYŻEJ24H” (kolumny: SNR, HU, POJEMNIK, DATA PRZYJĘCIA; opcjonalnie DATA WPROWADZENIA DANYCH) oraz „POJEMNIKI” (POJEMNIK, MAG) w Uzupełnij raport.</Text>
      </div>
    );
  }

  if (!hasRequiredColumns) {
    return (
      <div className="kpi-stat-page">
        <Title className="kpi-ps-header-title">Przyjęcie powyżej 24h</Title>
        <Text className="kpi-ps-table-empty">W arkuszu „POWYŻEJ24H” brak wymaganych kolumn: SNR, HU, DATA PRZYJĘCIA.</Text>
      </div>
    );
  }

  return (
    <div className="kpi-stat-page">
      <div className="kpi-ps-header-bar" style={{ marginBottom: 24 }}>
        <Title className="kpi-ps-header-title">Przyjęcie powyżej 24h</Title>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          {historyRows.length > 0 && (
            <div className="kpi-ps-zakres" style={{ marginBottom: 0 }}>
              <span className="kpi-ps-zakres-title">Zakres dat</span>
              <div className="kpi-ps-zakres-inner">
                <DatePicker id="przyjecie-date-from" label="Od:" value={dateFrom} onChange={(v) => setDateFrom(v || '')} max={dateTo || undefined} placeholder="Od" inline align="left" className="kpi-ps-zakres-date" />
                <DatePicker id="przyjecie-date-to" label="Do:" value={dateTo} onChange={(v) => setDateTo(v || '')} min={dateFrom || undefined} placeholder="Do" inline align="right" className="kpi-ps-zakres-date" />
              </div>
            </div>
          )}
          {idxDataWprowadzenia < 0 && (
            <Button size="xs" variant="secondary" onClick={saveSnapshot}>
              Zapisz bieżący raport jako odniesienie
            </Button>
          )}
        </div>
      </div>

      <Grid numItemsSm={1} numItemsMd={2} numItemsLg={4} className="kpi-ps-cards" style={{ marginBottom: 24 }}>
        <Card className="kpi-ps-kpi-card">
          <Text className="kpi-ps-kpi-label">Przyjęte od ostatniego raportu</Text>
          <Metric className="kpi-ps-kpi-metric">{referenceDate != null ? przyjeteCount : '—'}</Metric>
          {!referenceDate && (
            <Text className="kpi-ps-kpi-label" style={{ marginTop: 4, fontSize: 11 }}>
              {idxDataWprowadzenia >= 0
                ? 'Dodaj kolumnę „DATA WPROWADZENIA DANYCH” i wklej dane – wtedy pojawi się odniesienie.'
                : 'Zapisz raport jako odniesienie lub dodaj kolumnę „DATA WPROWADZENIA DANYCH”.'}
            </Text>
          )}
        </Card>
        <Card className="kpi-ps-kpi-card">
          <Text className="kpi-ps-kpi-label">KLT (nie przyjęte &gt;24h)</Text>
          <Metric className="kpi-ps-kpi-metric">{kltCount}</Metric>
        </Card>
        <Card className="kpi-ps-kpi-card">
          <Text className="kpi-ps-kpi-label">GLT (nie przyjęte &gt;24h)</Text>
          <Metric className="kpi-ps-kpi-metric">{gltCount}</Metric>
        </Card>
        <Card className="kpi-ps-kpi-card">
          <Text className="kpi-ps-kpi-label">Brak przyjęcia Powyżej 24h</Text>
          <Metric className="kpi-ps-kpi-metric">{kltCount + gltCount}</Metric>
        </Card>
      </Grid>

      <Grid numItemsSm={1} numItemsMd={2} numItemsLg={4} className="kpi-ps-cards kpi-ps-modules-equal" style={{ marginBottom: 24 }}>
        <Card className="kpi-ps-shell">
          <Text className="kpi-ps-kpi-label" style={{ marginBottom: 12 }}>Top 10 najstarsze (SNR + Magazyn)</Text>
          {top3Oldest.length === 0 ? (
            <Text className="kpi-ps-table-empty">Brak pozycji starszych niż 24h.</Text>
          ) : (
            <div className="kpi-ps-table">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>SNR</TableHeaderCell>
                    <TableHeaderCell>Magazyn</TableHeaderCell>
                    <TableHeaderCell>DATA PRZYJĘCIA</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {top3Oldest.map((r, i) => (
                    <TableRow key={`${r.snr}-${r.magazyn}-${i}`}>
                      <TableCell>{r.snr}</TableCell>
                      <TableCell>{r.magazyn}</TableCell>
                      <TableCell>{formatDateDisplay(r.date)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>

        <Card className="kpi-ps-shell">
          <Text className="kpi-ps-kpi-label" style={{ marginBottom: 12 }}>Ilość nie przyjętych KLT</Text>
          {historyRowsInRange.length >= 2 ? (
            <ResponsiveContainer width="100%" height={150}>
              <LineChart data={historyRowsInRange.map((r) => ({ label: formatHistoryDateShort(r.data), klt: r.klt }))} margin={{ top: 36, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} angle={historyRowsInRange.length > 7 ? -35 : 0} textAnchor={historyRowsInRange.length > 7 ? 'end' : 'middle'} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} domain={[0, 'auto']} />
                <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={{ color: '#e2e8f0' }} formatter={(v, name, props) => [`${props.payload?.label || ''} - ${v} KLT`, 'KLT']} />
                <Line type="monotone" dataKey="klt" name="KLT" stroke={CHART_COLORS.KLT} strokeWidth={2} dot={{ fill: CHART_COLORS.KLT, r: 3 }}>
                  <LabelList dataKey="klt" position="top" content={(p) => <ChartValueLabel {...p} dataKey="klt" color={CHART_COLORS.KLT} />} />
                </Line>
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Text className="kpi-ps-table-empty" style={{ padding: '16px 0', color: '#94a3b8' }}>
              {historyRows.length === 0 ? 'Brak danych historii.' : 'Wybierz zakres dat z co najmniej dwoma wpisami.'}
            </Text>
          )}
        </Card>

        <Card className="kpi-ps-shell">
          <Text className="kpi-ps-kpi-label" style={{ marginBottom: 12 }}>Ilość nie przyjętych GLT</Text>
          {historyRowsInRange.length >= 2 ? (
            <ResponsiveContainer width="100%" height={150}>
              <LineChart data={historyRowsInRange.map((r) => ({ label: formatHistoryDateShort(r.data), glt: r.glt }))} margin={{ top: 36, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} angle={historyRowsInRange.length > 7 ? -35 : 0} textAnchor={historyRowsInRange.length > 7 ? 'end' : 'middle'} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} domain={[0, 'auto']} />
                <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={{ color: '#e2e8f0' }} formatter={(v, name, props) => [`${props.payload?.label || ''} - ${v} GLT`, 'GLT']} />
                <Line type="monotone" dataKey="glt" name="GLT" stroke={CHART_COLORS.GLT} strokeWidth={2} dot={{ fill: CHART_COLORS.GLT, r: 3 }}>
                  <LabelList dataKey="glt" position="top" content={(p) => <ChartValueLabel {...p} dataKey="glt" color={CHART_COLORS.GLT} />} />
                </Line>
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Text className="kpi-ps-table-empty" style={{ padding: '16px 0', color: '#94a3b8' }}>
              {historyRows.length === 0 ? 'Brak danych historii.' : 'Wybierz zakres dat z co najmniej dwoma wpisami.'}
            </Text>
          )}
        </Card>

        <Card className="kpi-ps-shell">
          <Text className="kpi-ps-kpi-label" style={{ marginBottom: 12 }}>Towar nie przyjęty - SNR - Ilość pojemników</Text>
          {sumyPerSnr.length === 0 ? (
            <Text className="kpi-ps-table-empty">Brak danych.</Text>
          ) : (
            <div className={`kpi-ps-table ${sumyPerSnr.length > 10 ? 'kpi-ps-table-scroll' : ''}`} style={sumyPerSnr.length > 10 ? { maxHeight: 280 } : undefined}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>SNR</TableHeaderCell>
                    <TableHeaderCell>Magazyn</TableHeaderCell>
                    <TableHeaderCell>Liczba</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sumyPerSnr.map((r, i) => (
                    <TableRow key={`${r.snr}-${r.magazyn}-${i}`}>
                      <TableCell>{r.snr}</TableCell>
                      <TableCell>{r.magazyn}</TableCell>
                      <TableCell>{r.liczba}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </Grid>

      {historyRows.length > 0 && (
        <Card className="kpi-ps-shell kpi-ps-charts-area" style={{ marginTop: 32, marginBottom: 24 }}>
          <Text className="kpi-ps-charts-title" style={{ marginBottom: 4 }}>Historia / Trend</Text>

          {singleDayRow && (
            <Card className="kpi-ps-shell" style={{ marginBottom: 20 }}>
              <Text className="kpi-ps-kpi-label" style={{ marginBottom: 12 }}>Stan na dzień {singleDayRow.data}</Text>
              <Grid numItemsSm={2} numItemsLg={4} className="kpi-ps-cards">
                <Card className="kpi-ps-kpi-card">
                  <Text className="kpi-ps-kpi-label">KLT (nie przyjęte &gt;24h)</Text>
                  <Metric className="kpi-ps-kpi-metric">{singleDayRow.klt}</Metric>
                </Card>
                <Card className="kpi-ps-kpi-card">
                  <Text className="kpi-ps-kpi-label">GLT (nie przyjęte &gt;24h)</Text>
                  <Metric className="kpi-ps-kpi-metric">{singleDayRow.glt}</Metric>
                </Card>
                <Card className="kpi-ps-kpi-card">
                  <Text className="kpi-ps-kpi-label">Brak przyjęcia Powyżej 24h</Text>
                  <Metric className="kpi-ps-kpi-metric">{singleDayRow.razem}</Metric>
                </Card>
                <Card className="kpi-ps-kpi-card">
                  <Text className="kpi-ps-kpi-label">Przyjęte od poprzedniego raportu</Text>
                  <Metric className="kpi-ps-kpi-metric">{singleDayRow.przyjeteOdPoprzedniego}</Metric>
                </Card>
              </Grid>
              {(singleDayRow.najstarszaData || singleDayRow.unikalneSnr != null) && (
                <Text className="kpi-ps-kpi-label" style={{ marginTop: 8, fontSize: 11 }}>
                  {singleDayRow.najstarszaData ? `Najstarsza data przyjęcia: ${singleDayRow.najstarszaData}. ` : ''}
                  {singleDayRow.unikalneSnr != null ? `Unikalne SNR: ${singleDayRow.unikalneSnr}.` : ''}
                </Text>
              )}
            </Card>
          )}

          <Text className="kpi-ps-kpi-label" style={{ marginBottom: 8, marginTop: singleDayRow ? 24 : 0 }}>Tabela wpisów w wybranym zakresie</Text>
          <div className="kpi-ps-table" style={{ maxHeight: 320, overflow: 'auto', marginBottom: 24 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>DATA</TableHeaderCell>
                  <TableHeaderCell>KLT</TableHeaderCell>
                  <TableHeaderCell>GLT</TableHeaderCell>
                  <TableHeaderCell>RAZEM</TableHeaderCell>
                  <TableHeaderCell>PRZYJĘTE</TableHeaderCell>
                  <TableHeaderCell>PRZYJ. KLT</TableHeaderCell>
                  <TableHeaderCell>PRZYJ. GLT</TableHeaderCell>
                  <TableHeaderCell>NAJSTARSZA</TableHeaderCell>
                  <TableHeaderCell>UNIK. SNR</TableHeaderCell>
                  <TableHeaderCell>NOTATKA</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyRowsInRange.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="kpi-ps-table-empty-cell">
                      Brak wpisów w wybranym zakresie dat. Zmień zakres lub upewnij się, że w historii są dane z tych dni.
                    </TableCell>
                  </TableRow>
                ) : (
                  historyRowsInRange.map((r, i) => (
                    <TableRow key={`${r.data}-${i}`}>
                      <TableCell>{r.data}</TableCell>
                      <TableCell>{r.klt}</TableCell>
                      <TableCell>{r.glt}</TableCell>
                      <TableCell>{r.razem}</TableCell>
                      <TableCell>{r.przyjeteOdPoprzedniego}</TableCell>
                      <TableCell>{r.przyjeteKlt}</TableCell>
                      <TableCell>{r.przyjeteGlt}</TableCell>
                      <TableCell>{r.najstarszaData || '—'}</TableCell>
                      <TableCell>{r.unikalneSnr}</TableCell>
                      <TableCell style={{ maxWidth: 120 }}>{r.notatka || '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}

export default Przyjecie;
